import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { MAX_TREE_DEPTH, PROPERTY_NAMES } from "../constants";
import type { ChildrenProp, ExtendedTreeItem, TreeItem } from "../types";
import { BlockOperations } from "./BlockOperations";

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && isPlainObject(v);

const isChildrenProp = (prop: unknown): prop is ChildrenProp => {
  if (!isObj(prop)) return false;
  const maybe = prop as Partial<ChildrenProp> & {
    prop?: unknown;
    type?: unknown;
    value?: unknown;
  };
  const hasCorrectType = maybe.type === "children";
  const hasCorrectProp = maybe.prop === undefined || maybe.prop === "children";
  return hasCorrectType && hasCorrectProp && isArray(maybe.value);
};

const findAllChildrenWithNames = (
  blockProps: unknown,
  visited = new WeakSet<Record<string, unknown>>(),
): { propName: string; children: BaseBlock[] }[] => {
  const allChildrenGroups: { propName: string; children: BaseBlock[] }[] = [];
  if (!isObj(blockProps)) return allChildrenGroups;

  const queue: Record<string, unknown>[] = [blockProps];

  while (queue.length) {
    const current = queue.shift();
    if (!current) continue;
    if (visited.has(current)) continue;

    visited.add(current);

    for (const [key, prop] of Object.entries(current)) {
      if (isChildrenProp(prop)) {
        allChildrenGroups.push({
          propName: key,
          children: prop.value,
        });
        continue;
      }
      if (isObj(prop)) {
        queue.push(prop);
      }
    }
  }

  return allChildrenGroups;
};

const extractChildBlocks = (props: Record<string, unknown>): BaseBlock[] => {
  const childBlocks: BaseBlock[] = [];
  for (const prop of Object.values(props)) {
    if (isChildrenProp(prop)) {
      childBlocks.push(...prop.value);
    }
  }
  return childBlocks;
};

const createTreeItem = (block: BaseBlock): TreeItem => {
  const base: TreeItem = {
    id: block.id,
    type: block.type,
    collapsed: true,
  };

  const childrenGroups = findAllChildrenWithNames(block.props);

  if (!childrenGroups.length) {
    base.canHaveChildren = false;
    return base;
  }

  base.children = childrenGroups.map(group => ({
    id: `${block.id}-${group.propName}`,
    type: group.propName,
    collapsed: true,
    children: TreeOperations.convertBlocksToTree(group.children),
    isPropertyNode: true,
  }));

  return base;
};

const areTreeItemsEqual = (a: TreeItem, b: TreeItem): boolean => {
  if (a === b) return true;
  if (a.id !== b.id || a.type !== b.type) return false;

  const aChildren = a.children ?? [];
  const bChildren = b.children ?? [];

  if (aChildren.length !== bChildren.length) return false;

  for (let i = 0; i < aChildren.length; i++) {
    if (!areTreeItemsEqual(aChildren[i], bChildren[i])) return false;
  }

  return true;
};

export const TreeOperations = {
  isPropertyNode: (item: TreeItem): boolean =>
    (item as ExtendedTreeItem).isPropertyNode === true,
  isObj,
  isChildrenProp,
  getActualBlockId: (itemId: string): string => {
    const lastDashIndex = itemId.lastIndexOf("-");
    if (lastDashIndex === -1) return itemId;

    const lastPart = itemId.substring(lastDashIndex + 1);
    return PROPERTY_NAMES.has(lastPart)
      ? itemId.substring(0, lastDashIndex)
      : itemId;
  },

  parsePropertyNodeId: (
    propertyNodeId: string,
  ): { parentBlockId: string; propName: string } | null => {
    const lastDashIndex = propertyNodeId.lastIndexOf("-");
    if (lastDashIndex === -1) return null;

    return {
      propName: propertyNodeId.substring(lastDashIndex + 1),
      parentBlockId: propertyNodeId.substring(0, lastDashIndex),
    };
  },

  convertBlocksToTree: (blocks: BaseBlock[]): TreeItem[] =>
    blocks.map(block => createTreeItem(block)),

  convertTreeToBlocks: (
    treeItems: TreeItem[],
    originalBlocks: BaseBlock[],
  ): BaseBlock[] => {
    const blockMap = BlockOperations.buildBlockMap(originalBlocks);
    const usedBlockIds = new Set<string>();

    const rebuildFromTree = (items: TreeItem[]): BaseBlock[] =>
      items
        .map(item =>
          BlockOperations.processBlock(
            item,
            blockMap,
            usedBlockIds,
            rebuildFromTree,
          ),
        )
        .filter((block): block is BaseBlock => block !== null);

    return rebuildFromTree(treeItems);
  },

  findParentItem: (
    items: TreeItem[],
    targetId: string,
    parent: TreeItem | null = null,
  ): TreeItem | null => {
    if (!items.length) return null;
    const stack = [{ items, parent, depth: 0 }];

    while (stack.length) {
      const popped = stack.pop();
      if (!popped) continue;
      const { items: currentItems, parent: currentParent, depth } = popped;

      if (depth > MAX_TREE_DEPTH) return null;

      for (const item of currentItems) {
        if (item.id === targetId) return currentParent;

        if (item.children?.length) {
          stack.push({ items: item.children, parent: item, depth: depth + 1 });
        }
      }
    }

    return null;
  },

  findParentIds: (items: TreeItem[], targetId: string): Set<string> => {
    const parents = new Set<string>();
    const stack = [{ items, parentIds: [] as string[] }];

    while (stack.length) {
      const popped = stack.pop();
      if (!popped) continue;
      const { items: currentItems, parentIds } = popped;

      for (const item of currentItems) {
        if (item.id === targetId) {
          return new Set(parentIds);
        }
        if (item.children?.length) {
          stack.push({
            items: item.children,
            parentIds: [...parentIds, item.id],
          });
        }
      }
    }

    return parents;
  },

  areTreesEqual: (a: TreeItem[], b: TreeItem[]): boolean => {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (!areTreeItemsEqual(a[i], b[i])) return false;
    }
    return true;
  },

  updateCollapsedStateMap: (
    treeItems: TreeItem[],
    stateMap: Map<string, boolean>,
  ): void => {
    const stack = [...treeItems];
    while (stack.length) {
      const item = stack.pop();
      if (!item) continue;

      if (item.collapsed !== undefined) {
        stateMap.set(item.id, item.collapsed);
      }
      if (item.children?.length) {
        stack.push(...item.children);
      }
    }
  },

  applyCollapsedStateToTree: (
    treeItems: TreeItem[],
    stateMap: Map<string, boolean>,
  ): TreeItem[] => {
    const result: TreeItem[] = [];
    const stack = [{ items: treeItems, parent: result }];

    while (stack.length) {
      const current = stack.pop();
      if (!current) continue;

      const { items, parent } = current;

      for (const item of items) {
        const newItem: TreeItem = {
          ...item,
          collapsed: stateMap.get(item.id) ?? item.collapsed ?? true,
        };

        parent.push(newItem);

        if (item.children?.length) {
          newItem.children = [];
          stack.push({ items: item.children, parent: newItem.children });
        }
      }
    }
    return result;
  },

  shallowArrayEqual: <T>(arr1: T[] | null, arr2: T[]): boolean => {
    if (!arr1 || arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr2.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  },

  findAllChildrenWithNames,
  extractChildBlocks,
} as const;
